import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCheck, Menu as MenuIcon } from 'lucide-react';

// Route → page title mapping (kept here because routes are flat — no nesting)
const TITLES = {
  '/':              'Dashboard',
  '/pos':           'Point of Sale',
  '/kds':           'Kitchen Display',
  '/orders':        'Orders',
  '/tables':        'Tables',
  '/reservations':  'Reservations',
  '/menu':          'Menu',
  '/inventory':     'Inventory',
  '/customers':     'Customers',
  '/staff':         'Staff',
  '/payroll':       'Payroll',
  '/riders':        'Riders',
  '/analytics':     'Analytics',
  '/ai':            'AI Insights',
  '/warehouse':     'Warehouse',
  '/expenses':      'Expenses',
  '/users':         'User Management',
  '/outlets':       'Outlets',
  '/qrcode':        'QR Codes',
  '/settings':      'Settings',
};

const priorityDot = (p) => {
  switch (p) {
    case 'urgent': return 'bg-danger-500';
    case 'high':   return 'bg-warning-500';
    case 'medium': return 'bg-primary-500';
    default:       return 'bg-neutral-300';
  }
};

const formatRelativeTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get('/api/notifications?limit=20', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unreadCount || 0);
    } catch (_) {
      // Silent — auth or network issues just mean no notifications shown
    }
  };

  // Initial fetch + poll every 30s
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 30000);
    return () => clearInterval(id);
  }, []);

  // Close panel on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const id = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.put(`/api/notifications/${id}/read`, {}, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      fetchNotifications();
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await axios.put('/api/notifications/read-all', {}, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      fetchNotifications();
    } catch (_) {}
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-600 transition-colors"
        aria-label={`Notifications${unread ? ` — ${unread} unread` : ''}`}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 bg-danger-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-16 sm:top-full mt-0 sm:mt-2 w-auto sm:w-96 bg-white border border-neutral-200 rounded-lg shadow-card-hover z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">Notifications</h3>
              <p className="text-xs text-neutral-500">
                {unread > 0 ? `${unread} unread` : 'All caught up'}
              </p>
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-neutral-500">
                <Bell size={28} className="mx-auto text-neutral-300 mb-2" />
                <p>You don't have any notifications yet.</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n._id}
                  onClick={() => !n.isRead && markRead(n._id)}
                  className={`w-full text-left px-4 py-3 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors ${
                    !n.isRead ? 'bg-primary-50/40' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${priorityDot(n.priority)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-neutral-900 truncate">{n.title}</p>
                        {!n.isRead && (
                          <span className="text-[10px] font-semibold text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded uppercase flex-shrink-0">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-neutral-400 mt-1">{formatRelativeTime(n.createdAt)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TopBar = ({ onMenuClick }) => {
  const { pathname } = useLocation();
  const title = TITLES[pathname] || 'Chenab System';

  return (
    <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-3 sm:px-6 flex-shrink-0">
      <div className="flex items-center min-w-0">
        <button
          onClick={onMenuClick}
          className="md:hidden mr-2 w-9 h-9 -ml-1 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-600 flex-shrink-0"
          aria-label="Open menu"
        >
          <MenuIcon size={20} />
        </button>
        <span className="hidden sm:inline text-sm text-neutral-500">Pages</span>
        <span className="hidden sm:inline mx-2 text-neutral-300">/</span>
        <h2 className="text-sm font-semibold text-neutral-800 truncate">{title}</h2>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <NotificationBell />
      </div>
    </header>
  );
};

export default TopBar;
